import React from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";

import defaultSpecialPack from "../../../assets/images/temp/defaultSpecialPack.webp";
import { I18N_NAMESPACES } from "../../../helpers/constants";
import { getCashAmountLabel } from "../../../helpers/i18n";
import useIsAdventCalendarLandingEdition from "../../../helpers/useIsAdventCalendarLandingEdition";
import { SpecialPackProduct } from "../../../types/__generated__/SpecialPackProduct";
import URL_PATTERNS from "../../../urls";
import { AddButton, Card, Cover, Footer, Name, PanelBottom, Price, StockLabel } from "./style";

interface SpecialPackProductCardProps {
  product: SpecialPackProduct;
  isInCollection?: boolean;
  // Opens the pack's options tray for this option, like ADD on the wine pack cards
  onAdd?: (product: SpecialPackProduct) => void;
}

// The active advent calendar has its own landing page; other pack options open their edition
export const getDetailsUrl = (
  { slug, specialPackEditionSlug }: SpecialPackProduct,
  isAdventCalendarLanding: boolean
) => {
  if (isAdventCalendarLanding) return URL_PATTERNS.SPECIAL_PACK_ADVENT_CALENDAR;
  if (specialPackEditionSlug) return URL_PATTERNS.SPECIAL_PACK_DETAILS(specialPackEditionSlug);
  return URL_PATTERNS.PRODUCT_DETAILS_BY_SLUG(slug);
};

const SpecialPackProductCard = ({ product, isInCollection = false, onAdd }: SpecialPackProductCardProps) => {
  const { t } = useTranslation(I18N_NAMESPACES.COMMON);
  const isAdventCalendarLanding = useIsAdventCalendarLandingEdition(product.specialPackEditionSlug);
  // Pack options are titled by their edition's listing description, matching the wine pack cards;
  // the product name is the edition and option names joined, which is not a customer-facing title.
  const title = product.specialPackEditionListingDescription || product.name;
  const detailsUrl = getDetailsUrl(product, isAdventCalendarLanding);
  // Only pack options belong to an edition whose options the tray can offer
  const canAdd = !!onAdd && !!product.specialPackEditionSlug;

  return (
    <Card $isInCollection={isInCollection}>
      <Link to={detailsUrl}>
        <Cover src={product.coverPhotoMedium || defaultSpecialPack} alt={title} loading="lazy" />
      </Link>
      <Footer>
        <Link to={detailsUrl}>
          <Name>{title}</Name>
        </Link>
        <PanelBottom>
          <Price>{getCashAmountLabel(product.sellingPrice)}</Price>
          {canAdd &&
            (isAdventCalendarLanding ? (
              <AddButton as={Link} to={URL_PATTERNS.SPECIAL_PACK_ADVENT_CALENDAR}>
                {t("common:add")}
              </AddButton>
            ) : (
              <AddButton type="button" onClick={() => onAdd?.(product)} disabled={product.hasStock === false}>
                {t("common:add")}
              </AddButton>
            ))}
        </PanelBottom>
        {/* Advent calendar options sell while enabled, regardless of stock; the landing page owns that */}
        {product.hasStock === false && !isAdventCalendarLanding && (
          <StockLabel>{t("common:noStock")}</StockLabel>
        )}
      </Footer>
    </Card>
  );
};

export default SpecialPackProductCard;
